import { FormDatepicker, Loader } from '@/components'
import { FormProvider, useFormContext } from '@/providers'
import {
  usePublicCheckoutOverview,
  useUpdatePublicCheckoutOverview,
} from '@/services'
import { daysInFuture } from '@/utils'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { makeStartDateValidationSchema } from './validationSchema'

interface StepFormProps {
  setButtonState: (state: { onClick?: () => void; loading?: boolean }) => void
}

interface PublicCheckoutStartDateStepProps {
  onNext: () => void
  setButtonState: (state: { onClick?: () => void; loading?: boolean }) => void
}

interface CheckoutOverviewData {
  startDate?: string
  recommendationProductId?: string
}

interface FormValues {
  startDate: string
}

const StepForm = ({ setButtonState }: StepFormProps) => {
  const { categoryId } = useParams()
  const { submitForm, customValidate } = useFormContext()

  useEffect(() => {
    setButtonState({
      onClick: () =>
        customValidate({
          onSuccess: submitForm,
        }),
    })
  }, [])

  return (
    <FormDatepicker
      max={categoryId === 'LE' ? daysInFuture(90) : daysInFuture(14)}
      min={daysInFuture(1)}
      name="startDate"
      validateOnChange
    />
  )
}

export const PublicCheckoutStartDateStep = ({
  onNext,
  setButtonState,
}: PublicCheckoutStartDateStepProps) => {
  const { t } = useTranslation()
  const { categoryId } = useParams()
  const { data, isLoading } = usePublicCheckoutOverview()
  const typedData = data as CheckoutOverviewData | undefined

  const updateCheckoutOverviewMutation = useUpdatePublicCheckoutOverview(
    typedData?.recommendationProductId ?? '',
  )

  useEffect(() => {
    setButtonState({
      loading: updateCheckoutOverviewMutation.isLoading,
    })
  }, [updateCheckoutOverviewMutation.isLoading])

  const handleSubmit = (formValues: FormValues) => {
    updateCheckoutOverviewMutation.mutate(formValues, { onSuccess: onNext })
  }

  if (isLoading) return <Loader />

  return (
    <FormProvider
      initialValues={{ startDate: typedData?.startDate }}
      onSubmit={handleSubmit}
      validationSchema={makeStartDateValidationSchema(
        t('validations', { returnObjects: true }),
        categoryId as string,
      )}
    >
      <StepForm setButtonState={setButtonState} />
    </FormProvider>
  )
}
